import React from 'react';

export const Privacy: React.FC = () => (
  <div className="min-h-screen bg-white">
    <div className="max-w-3xl mx-auto px-6 py-16">
      <h1 className="text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
      <p className="text-sm text-gray-500 mb-10">Effective Date: February 24, 2026 · Last Updated: February 24, 2026</p>

      <div className="prose prose-gray max-w-none space-y-8">
        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">1. Introduction</h2>
          <p className="text-gray-700 leading-relaxed">
            Intrlock ("we," "us," or "our") operates the Intrlock platform at intrlock.io, providing
            access control, visitor management, and video intercom services for residential and commercial
            properties. This Privacy Policy explains how we collect, use, store, and protect your personal
            information.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">2. Information We Collect</h2>
          <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">Account Information</h3>
          <p className="text-gray-700 leading-relaxed">
            When you create an account, we collect your name, email address, phone number, and
            organization/company name. Property managers and integrators may also provide business
            address and billing information.
          </p>
          <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">Property & Tenant Data</h3>
          <p className="text-gray-700 leading-relaxed">
            We store property addresses, unit numbers, tenant names, phone numbers, and email addresses
            as provided by property managers. This data is used solely to operate the visitor directory
            and intercom system.
          </p>
          <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">Visitor Interaction Data</h3>
          <p className="text-gray-700 leading-relaxed">
            When a visitor uses the intercom system, we process the call session (video/audio) in
            real-time to connect visitors with tenants. We do not record or store video or audio from
            intercom calls. We may log call metadata (timestamp, duration, property ID) for system
            reliability and troubleshooting.
          </p>
          <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">Device & Access Data</h3>
          <p className="text-gray-700 leading-relaxed">
            For access control features, we process door lock commands and may log access events
            (unlock timestamps, device identifiers) for security audit purposes.
          </p>
          <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">Usage & Technical Data</h3>
          <p className="text-gray-700 leading-relaxed">
            We automatically collect standard technical information including IP addresses, browser type,
            device type, and pages visited. This is used for security monitoring and service improvement.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">3. How We Use Your Information</h2>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li>To operate and maintain the Intrlock platform and its features</li>
            <li>To facilitate visitor-to-tenant communication via the intercom system</li>
            <li>To process door unlock requests and manage access control</li>
            <li>To send SMS or other notifications related to incoming visitor calls</li>
            <li>To process payments and manage subscriptions via Stripe</li>
            <li>To provide customer support and respond to inquiries</li>
            <li>To monitor system health, prevent abuse, and ensure security</li>
            <li>To comply with legal obligations</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">4. We Do Not Share or Sell Your Data</h2>
          <p className="text-gray-700 leading-relaxed">
            <strong>We do not sell, rent, trade, or otherwise share your personal information with
            third parties for marketing or advertising purposes.</strong> Your data is used exclusively
            to provide and improve the Intrlock service.
          </p>
          <p className="text-gray-700 leading-relaxed mt-3">
            We use the following service providers strictly to operate the platform, and they process
            data only on our behalf and under our instructions:
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
            <li><strong>Amazon Web Services</strong> — secure application and database hosting</li>
            <li><strong>Amazon Web Services (AWS)</strong> — cloud infrastructure and serverless functions</li>
            <li><strong>Telnyx</strong> — video communication and SMS delivery</li>
            <li><strong>Stripe</strong> — payment processing (we never store credit card numbers)</li>
            <li><strong>CloudFront</strong> — content delivery</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mt-3">
            We may disclose information if required by law, court order, or governmental regulation,
            or if necessary to protect the safety of our users or the public.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">5. Data Security</h2>
          <p className="text-gray-700 leading-relaxed">
            We implement industry-standard security measures to protect your data, including:
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
            <li>HTTPS/TLS encryption for all data in transit</li>
            <li>Encrypted database storage</li>
            <li>Role-based access controls limiting data visibility</li>
            <li>Regular security monitoring and access logging</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">6. Data Retention</h2>
          <p className="text-gray-700 leading-relaxed">
            We retain account and property data for as long as your account is active or as needed to
            provide services. Call metadata logs are retained for up to 90 days. If you request account
            deletion, we will remove your personal data within 30 days, except where retention is
            required by law.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">7. Your Rights</h2>
          <p className="text-gray-700 leading-relaxed">You have the right to:</p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
            <li>Access the personal data we hold about you</li>
            <li>Request correction of inaccurate data</li>
            <li>Request deletion of your data</li>
            <li>Opt out of non-essential communications</li>
            <li>Export your data in a portable format</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mt-3">
            To exercise any of these rights, contact us at <a href="mailto:privacy@intrlock.io" className="text-emerald-600 hover:underline">privacy@intrlock.io</a>.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">8. Cookies</h2>
          <p className="text-gray-700 leading-relaxed">
            We use essential cookies to maintain your login session and preferences. We do not use
            third-party tracking cookies or advertising cookies.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">9. Children's Privacy</h2>
          <p className="text-gray-700 leading-relaxed">
            Intrlock is not directed at children under 13. We do not knowingly collect personal
            information from children. If we learn that we have collected data from a child under 13,
            we will delete it promptly.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">10. Changes to This Policy</h2>
          <p className="text-gray-700 leading-relaxed">
            We may update this Privacy Policy from time to time. We will notify users of material
            changes via email or a notice on the platform. Continued use of the service after changes
            constitutes acceptance of the updated policy.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">11. Contact Us</h2>
          <p className="text-gray-700 leading-relaxed">
            If you have questions about this Privacy Policy or our data practices, please contact us at:
          </p>
          <p className="text-gray-700 mt-2">
            <strong>Intrlock</strong><br />
            Email: <a href="mailto:privacy@intrlock.io" className="text-emerald-600 hover:underline">privacy@intrlock.io</a><br />
            Website: <a href="https://intrlock.io" className="text-emerald-600 hover:underline">intrlock.io</a>
          </p>
        </section>
      </div>
    </div>
  </div>
);

export default Privacy;
